import React, { useEffect, useRef, useState } from "react";
import {
  Loader2, ArrowRight, ArrowLeft, Check, CheckCircle2, Camera, User as UserIcon,
  Phone, CalendarDays, Award, Plus, X, Rocket, Sparkles, Truck, Headset,
} from "lucide-react";
import { toast } from "sonner";
import { staffOnboardingApi, profileApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const defaultAvailability = () =>
  DAYS.reduce((acc, d) => {
    const work = d !== "Sat" && d !== "Sun";
    acc[d] = { active: work, start: work ? "08:00" : "", end: work ? "17:00" : "" };
    return acc;
  }, {});

const ROLE_COPY = {
  technician: { icon: Truck, title: "Welcome to the field team", sub: "Set up your profile and availability so dispatch can route the right jobs to you." },
  dispatcher: { icon: Headset, title: "Welcome to the dispatch desk", sub: "A quick setup and a tour of where everything lives." },
  office_personnel: { icon: Sparkles, title: "Welcome aboard", sub: "Let's set up your profile and show you around." },
  admin: { icon: Sparkles, title: "Welcome to the team", sub: "A quick setup and a tour to get you going." },
};

const TOUR_POINTS = {
  dispatcher: [
    "Dispatch Board — drag jobs onto technicians and watch the day fill up.",
    "Jobs & Customers — create work orders and manage the customer file.",
    "Messages — chat with techs in the field in real time.",
  ],
  office_personnel: [
    "Pipeline — track and follow up on your assigned leads.",
    "Customers — keep customer records and notes up to date.",
    "Tasks — see everything assigned to you in one place.",
  ],
  admin: [
    "Reports — revenue, payroll and accounting at a glance.",
    "Staff & Billing — invite teammates and manage seats.",
    "Marketing & Automations — grow and re-engage customers.",
  ],
};

export default function StaffOnboarding() {
  const { user, hasModule, refreshMe } = useAuth();
  const [state, setState] = useState(null); // backend state
  const [visible, setVisible] = useState(false);
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // form
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [availability, setAvailability] = useState(defaultAvailability());
  const [certs, setCerts] = useState([]);
  const [certInput, setCertInput] = useState("");

  useEffect(() => {
    if (!user || user === false || user.role === "super_admin" || !hasModule("onboarding_checklists")) {
      setVisible(false);
      return;
    }
    let cancelled = false;
    staffOnboardingApi
      .state()
      .then((s) => {
        if (cancelled) return;
        if (!s.needed) {
          setVisible(false);
          return;
        }
        setState(s);
        setSteps(s.steps || ["welcome", "profile", "done"]);
        setIdx(0);
        setName(s.profile?.name || s.name || "");
        setPhone(s.profile?.phone || "");
        setAvatarUrl(s.profile?.avatarUrl || null);
        setAvailability(s.profile?.availability || defaultAvailability());
        setCerts(s.profile?.certifications || []);
        setVisible(true);
      })
      .catch(() => setVisible(false));
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!visible || !state) return null;

  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const role = state.role;
  const copy = ROLE_COPY[role] || ROLE_COPY.admin;

  const saveStep = async () => {
    const payload = {};
    if (step === "profile") {
      payload.name = name.trim() || undefined;
      payload.phone = phone.trim() || undefined;
    } else if (step === "availability") {
      payload.availability = availability;
    } else if (step === "skills") {
      payload.certifications = certs;
    }
    if (Object.keys(payload).length) {
      await staffOnboardingApi.save(payload);
    }
  };

  const next = async () => {
    setBusy(true);
    try {
      await saveStep();
      setIdx((i) => Math.min(i + 1, steps.length - 1));
    } catch (_) {
      toast.error("Could not save — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const back = () => setIdx((i) => Math.max(0, i - 1));

  const finish = async () => {
    setBusy(true);
    try {
      await staffOnboardingApi.complete();
      await refreshMe();
      setVisible(false);
      toast.success("You're all set! Welcome aboard. 🎉");
    } catch (_) {
      toast.error("Something went wrong finishing setup.");
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await staffOnboardingApi.complete();
      await refreshMe();
      setVisible(false);
    } catch (_) {
      setBusy(false);
    }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await profileApi.uploadAvatar(file);
      setAvatarUrl(res.avatarUrl);
      toast.success("Profile photo added");
    } catch (_) {
      toast.error("Upload failed — try a JPG or PNG under 8MB.");
    } finally {
      setUploading(false);
    }
  };

  const addCert = () => {
    const v = certInput.trim();
    if (!v) return;
    if (!certs.some((c) => (c.name || "").toLowerCase() === v.toLowerCase())) {
      setCerts((list) => [...list, { name: v }]);
    }
    setCertInput("");
  };

  const initials = (name || state.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const totalSteps = steps.length;
  const pct = Math.round(((idx + 1) / totalSteps) * 100);

  return (
    <div
      data-testid="staff-onboarding"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#070B16]/80 backdrop-blur-md p-4"
    >
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="relative px-7 pt-7 pb-6 bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#0EA5E9]">
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
          <div className="relative flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center">
              <copy.icon className="h-6 w-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="text-white font-bold text-[18px] leading-tight">{copy.title}</div>
              <div className="text-blue-100 text-[12.5px]">{state.profile?.name ? `Hi ${(name || state.name || "").split(" ")[0]} · ` : ""}Step {idx + 1} of {totalSteps}</div>
            </div>
          </div>
          <div className="relative mt-4 h-1.5 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full bg-white transition-all duration-500" style={{ width: `${pct}%` }} data-testid="onb-progress" />
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6 min-h-[260px]" data-testid={`onb-step-${step}`}>
          {step === "welcome" && (
            <div className="text-center py-4">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                <Rocket className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="text-[19px] font-bold text-slate-900 mb-1.5">Let's get you set up</h3>
              <p className="text-[13.5px] text-slate-500 leading-relaxed max-w-sm mx-auto">{copy.sub}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-400">
                <Sparkles className="h-3.5 w-3.5" /> Takes less than a minute
              </div>
            </div>
          )}

          {step === "profile" && (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="onb-avatar-btn"
                  className="relative h-20 w-20 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-[22px] font-bold overflow-hidden group shrink-0"
                >
                  {avatarUrl ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api${avatarUrl.replace(/^\/api/, "")}`} alt="" className="h-full w-full object-cover" />
                  ) : (
                    initials
                  )}
                  <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
                  </span>
                </button>
                <div>
                  <div className="text-[14px] font-semibold text-slate-900">Profile photo</div>
                  <div className="text-[12px] text-slate-500 mb-1.5">Helps teammates recognize you.</div>
                  <button type="button" onClick={() => fileRef.current?.click()} className="text-[12.5px] font-semibold text-blue-600 hover:text-blue-700">
                    {avatarUrl ? "Change photo" : "Upload a photo"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickAvatar} data-testid="onb-avatar-input" />
                </div>
              </div>
              <Labeled label="Display name" icon={UserIcon}>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="onb-name" placeholder="Your name" className={inputCls} />
              </Labeled>
              <Labeled label="Mobile phone" icon={Phone}>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="onb-phone" placeholder="(555) 123-4567" className={inputCls} />
              </Labeled>
            </div>
          )}

          {step === "availability" && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-blue-600" />
                <h3 className="text-[14px] font-semibold text-slate-900">Your weekly availability</h3>
              </div>
              <p className="text-[12px] text-slate-500 mb-3">Toggle the days you work and set your hours. You can change this later.</p>
              <div className="space-y-1.5">
                {DAYS.map((d) => {
                  const day = availability[d] || { active: false, start: "", end: "" };
                  return (
                    <div key={d} className="flex items-center gap-3 py-1" data-testid={`onb-day-${d}`}>
                      <button
                        type="button"
                        onClick={() => setAvailability((a) => ({ ...a, [d]: { ...day, active: !day.active, start: !day.active && !day.start ? "08:00" : day.start, end: !day.active && !day.end ? "17:00" : day.end } }))}
                        data-testid={`onb-day-toggle-${d}`}
                        className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${day.active ? "bg-blue-600" : "bg-slate-200"}`}
                      >
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${day.active ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                      <span className="w-9 text-[13px] font-semibold text-slate-700">{d}</span>
                      {day.active ? (
                        <div className="flex items-center gap-1.5 flex-1">
                          <input type="time" value={day.start} onChange={(e) => setAvailability((a) => ({ ...a, [d]: { ...day, start: e.target.value } }))} className="h-8 px-2 rounded-lg border border-slate-200 text-[12.5px] flex-1" />
                          <span className="text-slate-400 text-[12px]">to</span>
                          <input type="time" value={day.end} onChange={(e) => setAvailability((a) => ({ ...a, [d]: { ...day, end: e.target.value } }))} className="h-8 px-2 rounded-lg border border-slate-200 text-[12.5px] flex-1" />
                        </div>
                      ) : (
                        <span className="text-[12px] text-slate-400 flex-1">Day off</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === "skills" && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Award className="h-4 w-4 text-blue-600" />
                <h3 className="text-[14px] font-semibold text-slate-900">Skills & certifications</h3>
              </div>
              <p className="text-[12px] text-slate-500 mb-3">Add your trade skills or licenses (e.g. EPA 608, HVAC, Master Plumber). Optional.</p>
              <div className="flex items-center gap-2 mb-3">
                <input
                  value={certInput}
                  onChange={(e) => setCertInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCert(); } }}
                  data-testid="onb-cert-input"
                  placeholder="Type a skill and press Add"
                  className={inputCls}
                />
                <button type="button" onClick={addCert} data-testid="onb-cert-add" className="h-10 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shrink-0 inline-flex items-center gap-1 text-[13px] font-semibold">
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {certs.length === 0 && <span className="text-[12.5px] text-slate-400">No skills added yet.</span>}
                {certs.map((c, i) => (
                  <span key={i} data-testid={`onb-cert-${i}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[12.5px] font-medium">
                    {c.name}
                    <button type="button" onClick={() => setCerts((list) => list.filter((_, j) => j !== i))} className="hover:text-blue-900">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {step === "tour" && (
            <div>
              <h3 className="text-[15px] font-bold text-slate-900 mb-1">Here's where everything lives</h3>
              <p className="text-[12.5px] text-slate-500 mb-4">A few places you'll use most.</p>
              <ul className="space-y-2.5">
                {(TOUR_POINTS[role] || TOUR_POINTS.admin).map((t, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50">
                    <div className="h-6 w-6 rounded-lg bg-blue-600 text-white flex items-center justify-center text-[11px] font-bold shrink-0">{i + 1}</div>
                    <span className="text-[13px] text-slate-700 leading-snug">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-9 w-9 text-emerald-500" />
              </div>
              <h3 className="text-[20px] font-bold text-slate-900 mb-1.5">You're all set!</h3>
              <p className="text-[13.5px] text-slate-500 max-w-xs mx-auto">Your profile is ready. Jump in and start working.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/60">
          <div>
            {idx > 0 && step !== "done" ? (
              <button type="button" onClick={back} data-testid="onb-back" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : step !== "done" ? (
              <button type="button" onClick={skip} disabled={busy} data-testid="onb-skip" className="text-[12.5px] font-medium text-slate-400 hover:text-slate-600">
                Skip for now
              </button>
            ) : <span />}
          </div>
          <div>
            {step === "done" ? (
              <button type="button" onClick={finish} disabled={busy} data-testid="onb-finish" className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Get started
              </button>
            ) : (
              <button type="button" onClick={next} disabled={busy} data-testid="onb-next" className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{idx === steps.length - 2 ? "Finish" : "Continue"} <ArrowRight className="h-4 w-4" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

function Labeled({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-1.5">
        <Icon className="h-3.5 w-3.5 text-slate-400" /> {label}
      </label>
      {children}
    </div>
  );
}
